"use client"

import { useState, useEffect } from "react"
import { ChevronDown, Building, LogOut, MenuIcon } from "lucide-react"
import Menu from "./Menu"
import { Outlet } from "react-router-dom"
import { getSucursalesByEmpresaId } from "../services/sucursalService"
import CustomSelect from "./CustomSelect"
import "./Menu.css"
import "./SucursalSelector.css"

const MainLayout = () => {
  const name = localStorage.getItem("nombreEmpleado")
  const empresaId = localStorage.getItem("idempresa")
  const [selectedMenu, setSelectedMenu] = useState("MENU")
  const [sucursales, setSucursales] = useState([])
  const [selectedSucursal, setSelectedSucursal] = useState(localStorage.getItem("idsucursal") || "")
  const [sucursalNombre, setSucursalNombre] = useState("Sucursal Central")
  const [showIndicator, setShowIndicator] = useState(true)
  const [showDropdown, setShowDropdown] = useState(false)
  const [isChanging, setIsChanging] = useState(false) // New state for transition effect
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  // Obtener las sucursales cuando el componente se monte
  useEffect(() => {
    const fetchSucursales = async () => {
      try {
        if (empresaId) {
          const data = await getSucursalesByEmpresaId(empresaId)
          setSucursales(data)
          if (data.length > 0) {
            const initialSucursalId = localStorage.getItem("idsucursal") || data[0].id
            setSelectedSucursal(initialSucursalId)

            const sucursal = data.find((s) => s.id === initialSucursalId)
            if (sucursal) {
              setSucursalNombre(sucursal.nombre)
            }
          }
        }
      } catch (error) {
        console.error("Error al cargar las sucursales:", error)
        // Keep the default "Sucursal Central" if there's an error
      }
    }

    fetchSucursales()
  }, [empresaId])

  // Efecto para ocultar el indicador después de un tiempo
  useEffect(() => {
    // Mostrar el indicador cuando cambia la sucursal
    setShowIndicator(true)

    // Add transition effect when changing branches
    if (sucursalNombre !== "Sucursal Central") {
      setIsChanging(true)
      setTimeout(() => {
        setIsChanging(false)
      }, 800)
    }

    // Configurar un temporizador para ocultar el indicador después de 5 segundos
    const timer = setTimeout(() => {
      setShowIndicator(false)
    }, 5000)

    // Limpiar el temporizador si el componente se desmonta o cambia la sucursal
    return () => clearTimeout(timer)
  }, [sucursalNombre]) // Se ejecuta cuando cambia la sucursal

  const handleMenuClick = (menuName) => {
    setSelectedMenu(menuName)
  }

  const handleSucursalChange = (event) => {
    const sucursalId = event.target.value

    // Set changing state first for smooth transition
    setIsChanging(true)

    setSelectedSucursal(sucursalId)
    localStorage.setItem("idsucursal", sucursalId)

    const sucursal = sucursales.find((s) => s.id === sucursalId)
    if (sucursal) {
      setSucursalNombre(sucursal.nombre)
    }

    // Dispatch a custom event to notify components about the sucursal change
    const sucursalChangeEvent = new CustomEvent("sucursalChange", {
      detail: { sucursalId },
    })
    window.dispatchEvent(sucursalChangeEvent)
  }

  // Mostrar el indicador manualmente
  const handleShowIndicator = () => {
    setShowIndicator(true)
    // Ocultar después de 5 segundos
    setTimeout(() => setShowIndicator(false), 5000)
  }

  const handleLogout = () => {
    localStorage.removeItem("token")
    localStorage.removeItem("idEmpleado")
    localStorage.removeItem("nombreEmpleado")
    localStorage.removeItem("role")
    localStorage.removeItem("idempresa")
    localStorage.removeItem("idsucursal")
    // Navigate to login page
    window.location.href = "/"
  }

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen)
  }

  // Formatea las sucursales para el CustomSelect
  const sucursalOptions = sucursales.map((sucursal) => ({
    value: sucursal.id,
    label: sucursal.nombre,
  }))

  useEffect(() => {
    const handleClickOutside = (event) => {
      const userProfile = document.querySelector(".user-profile")
      if (userProfile && !userProfile.contains(event.target)) {
        setShowDropdown(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [])

  return (
    <div className="home-container">
      {/* Encabezado */}
      <header className={`header ${mobileMenuOpen ? "mobile-menu-open" : ""}`}>
        <div className="header-left">
          <div className="logo">
            <span className="logo-icon">🔘</span>
            <span className="logo-text">Parking Club</span>
          </div>
        </div>

        <button
          className="mobile-menu-toggle"
          onClick={toggleMobileMenu}
          style={{
            display: "none", // Hidden by default, shown via media query
            background: "transparent",
            border: "none",
            color: "white",
            cursor: "pointer",
            padding: "8px",
          }}
        >
          <MenuIcon size={24} />
        </button>

        <div className="header-right">
          {/* Selector de sucursales - Minimalist Design */}
          <div className={`sucursal-selector ${isChanging ? "changing" : ""}`}>
            <Building className="sucursal-icon" size={16} />
            <CustomSelect
              options={sucursalOptions}
              value={selectedSucursal}
              onChange={handleSucursalChange}
              placeholder="Seleccionar"
              className={isChanging ? "changing" : ""}
            />
          </div>

          <div className="user-profile" onClick={() => setShowDropdown(!showDropdown)}>
            <span>{name}</span>
            <ChevronDown className="dropdown-icon" />

            {showDropdown && (
              <div className="user-dropdown">
                <div className="dropdown-item" onClick={handleLogout}>
                  <LogOut size={16} />
                  <span>Cerrar sesión</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Contenido Principal */}
      <div className="main-content">
        {/* Sidebar (Menu) */}
        <Menu selectedMenu={selectedMenu} onMenuClick={handleMenuClick} />

        {/* Área de Contenido */}
        <main className="content">
          {/* Indicador de sucursal con diseño minimalista */}
          <div
            className={`current-sucursal-indicator ${showIndicator ? "visible" : "hidden"} ${isChanging ? "changing" : ""}`}
          >
            <Building size={16} className="indicator-icon" />
            <span className="indicator-text">{sucursalNombre}</span>
          </div>

          {/* Botón para mostrar información de sucursal */}
          {!showIndicator && (
            <button
              className="sucursal-info-button"
              onClick={handleShowIndicator}
              title={sucursalNombre}
              aria-label="Mostrar información de sucursal"
            >
              <Building size={14} />
            </button>
          )}

          <Outlet />
        </main>
      </div>
    </div>
  )
}

export default MainLayout
